import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, Type } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NoDataMessageComponent } from '../../components/no-data-message/no-data-message.component';
import { NotificationsStateService } from '../../state/notifications-state.service';
import { NotificationBaseComponent } from './components/notification-base/notification-base.component';
import { NotificationEnrollmentAssignedComponent } from './components/notification-enrollment-assigned.component';
import { NotificationEnrollmentExtraLessonsComponent } from './components/notification-enrollment-extra-lessons.component';
import { NotificationEnrollmentLearnerReassignedComponent } from './components/notification-enrollment-learner-reassigned.component';
import { NotificationEnrollmentOnLeaveComponent } from './components/notification-enrollment-on-leave.component';
import { NotificationEnrollmentPausedComponent } from './components/notification-enrollment-paused.component';
import { NotificationEnrollmentProgramChangedComponent } from './components/notification-enrollment-program-changed.component';
import { NotificationMissingAttendanceComponent } from './components/notification-missing-attendance.component';
import { NotificationRescheduleProposalComponent } from './components/notification-reschedule-proposal.component';
import { NotificationRescheduledComponent } from './components/notification-rescheduled.component';
import { NotificationFinalGradeRequiredComponent } from './components/notification-final-grade-required.component';
import { NotificationMidSemesterSurveyIncompleteComponent } from './components/notification-mid-semester-survey-incomplete.component';
import { NotificationLessonGradesAvailableComponent } from './components/notification-lesson-grades-available.component';
import { NotificationSemesterGradesAvailableComponent } from './components/notification-semester-grades-available.component';
import { LoaderComponent } from '../loader/loader.component';
import { DEFAULT_NOTIFICATION_PRIORITY, NotificationType } from '@lb/api/mylb/enums';

@Component({
	standalone: true,
	imports: [CommonModule, RouterModule, NoDataMessageComponent, LoaderComponent],
	selector: 'lb-notifications',
	templateUrl: './notifications.component.html',
	styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit {
	_notificationState: NotificationsStateService = inject(NotificationsStateService);

	includedNotifications = input<string[]>([]);

	loading = this._notificationState.loading;
	loaded = this._notificationState.loaded;

	private componentDictionary: { [key: string]: Type<any> } = {
		[NotificationType.LessonRescheduled]: NotificationRescheduledComponent,
		[NotificationType.LessonRescheduleProposal]: NotificationRescheduleProposalComponent,
		[NotificationType.MissingAttendance]: NotificationMissingAttendanceComponent,

		[NotificationType.FinalGradeSubmissionRequired]: NotificationFinalGradeRequiredComponent,
		[NotificationType.MidSemesterSurveyIncomplete]: NotificationMidSemesterSurveyIncompleteComponent,
		[NotificationType.LessonGradesAvailable]: NotificationLessonGradesAvailableComponent,
		[NotificationType.SemesterGradesAvailable]: NotificationSemesterGradesAvailableComponent,

		[NotificationType.EnrollmentAssigned]: NotificationEnrollmentAssignedComponent,
		[NotificationType.EnrollmentPaused]: NotificationEnrollmentPausedComponent,
		[NotificationType.EnrollmentOnLeave]: NotificationEnrollmentOnLeaveComponent,
		[NotificationType.EnrollmentLearnerReassigned]: NotificationEnrollmentLearnerReassignedComponent,
		[NotificationType.EnrollmentExtraLessons]: NotificationEnrollmentExtraLessonsComponent,
		[NotificationType.EnrollmentProgramChanged]: NotificationEnrollmentProgramChangedComponent,
		default: NotificationBaseComponent
	};

	notifications = computed(() => {
		// Copy before sorting — the source array is owned by the signal store and must not be mutated.
		// Sort by priority ascending (lower = more urgent), then most recent first. Any type missing a
		// priority falls back to the bottom rather than producing NaN in the comparator.
		return [...this._notificationState.notifications()]
			.sort((a, b) => {
				const priorityA = a.priority ?? DEFAULT_NOTIFICATION_PRIORITY;
				const priorityB = b.priority ?? DEFAULT_NOTIFICATION_PRIORITY;
				if (priorityA !== priorityB) {
					return priorityA - priorityB;
				}
				return new Date(b.createDate).getTime() - new Date(a.createDate).getTime();
			})
			.map((a) => {
				return {
					component: this.componentDictionary[a.notificationType] || this.componentDictionary['default'],
					inputs: { notification: a }
				};
			});
	});

	ngOnInit() {
		this._notificationState.loadNotifications(false);
	}
}
